/**
 * More Page — full-screen tab at `?tab=more`.
 * Replaces the former VisitorMenu bottom-sheet. Rendered inside VisitorLayout
 * so it has direct access to layout-scoped data.
 *
 * Sub-pages via `?tab=more&view=faqs|sponsors` — main-view content below.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart } from "@fortawesome/pro-solid-svg-icons/faHeart";
import { faListCheck } from "@fortawesome/pro-solid-svg-icons/faListCheck";
import { faCircleQuestion } from "@fortawesome/pro-solid-svg-icons/faCircleQuestion";
import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import type {
  VisitorMenuConfig,
  FAQConfig,
  MenuItem,
  MenuSection,
} from "@wondermaps/db/schema";
import type { OperatingConfig, OperatingMode } from "@wondermaps/maps";
import { cn } from "@wondermaps/ui";
import { getTranslatedValue, type NameTranslations } from "@wondermaps/db/client";
import { VisitorPageHeader } from "./VisitorPageHeader";
import { FaqSubPage } from "./FaqSubPage";
import { SponsorsSubPage } from "./SponsorsSubPage";
import { OperatingHoursMenuSection } from "./OperatingHoursMenuSection";
import { VisitorMenuSection } from "./VisitorMenuSection";
import { SocialLinksRow } from "./SocialLinksRow";
import { LanguagePicker } from "./LanguagePicker";
import { ConsentModal } from "./ConsentModal";
import type { EmergencyAction } from "./VisitorMenu";
import type { SponsorLogo, SponsorTierGroup } from "./sponsor-types";
import type { VisitorNotification } from "./notification-types";
import type { VisitorSearchParams } from "../../lib/visitor-search";
import type { SupportedLanguage } from "../../i18n/config";
import { useAnalyticsConsent } from "../../hooks/useAnalyticsConsent";
import {
  getIconDefinition,
  registerThemeIcons,
} from "../../data/fontawesome-icons";
import { analytics } from "../../lib/analytics";

export interface MorePageProps {
  mapSlug: string;
  logoUrl?: string;
  appName?: string;
  orgName: string;
  notifications: VisitorNotification[];
  onBellClick: () => void;
  isTrayOpen: boolean;
  shareUrl: string;
  shareTitle: string;
  shareText?: string;
  menuConfig: VisitorMenuConfig;
  faqConfig?: FAQConfig;
  showAttribution: boolean;
  operatingMode?: OperatingMode;
  operatingConfig?: OperatingConfig | null;
  timezone?: string;
  emergencyActions?: EmergencyAction[];
  onEmergencyAction?: (action: EmergencyAction) => void;
  savedCount?: number;
  onSavedClick?: () => void;
  curatedListsCount?: number;
  onCuratedListsClick?: () => void;
  sponsors?: SponsorTierGroup[];
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
  onSponsorImpression?: (sponsors: SponsorLogo[], placement: string) => void;
  getSponsorPoiName?: (poiId: string) => string | undefined;
  onSponsorPoiClick?: (poiId: string) => void;
  multilingualEnabled?: boolean;
  availableLanguages?: SupportedLanguage[];
  /** Resolved accent for the secondary follow-CTA outline + text (null = no brand) */
  brandAccentCtaSecondary?: string | null;
  /** Resolved accent for menu icons (Saved/Curated/FAQ); null = use hardcoded defaults */
  brandAccentMenuIcons?: string | null;
  /** Master toggle for branded social card — non-null = circles + glyph contrast + brand follow text */
  brandAccentSocialCard?: string | null;
  /** Resolved color-mix string for social card outer panel bg (opacity-driven) */
  brandAccentSocialCardBg?: string | null;
  brandAccentPoweredBy?: string | null;
  /** Ungated org/theme primary color — used as CTA primary bg fallback */
  brandPrimary?: string | null;
}

export function MorePage(props: MorePageProps) {
  const { view } = useSearch({ strict: false }) as VisitorSearchParams;

  if (view === "faqs") {
    return (
      <FaqSubPage
        notifications={props.notifications}
        onBellClick={props.onBellClick}
        isTrayOpen={props.isTrayOpen}
        mapSlug={props.mapSlug}
        faqConfig={props.faqConfig}
      />
    );
  }

  if (view === "sponsors") {
    return (
      <SponsorsSubPage
        notifications={props.notifications}
        onBellClick={props.onBellClick}
        isTrayOpen={props.isTrayOpen}
        mapSlug={props.mapSlug}
        sponsors={props.sponsors}
        onSponsorClick={props.onSponsorClick}
        getPoiName={props.getSponsorPoiName}
        onPoiClick={props.onSponsorPoiClick}
      />
    );
  }

  return <MorePageMain {...props} />;
}

function MorePageMain({
  mapSlug,
  logoUrl,
  appName,
  orgName,
  notifications,
  onBellClick,
  isTrayOpen,
  shareUrl,
  shareTitle,
  shareText,
  menuConfig,
  faqConfig,
  showAttribution,
  operatingMode,
  operatingConfig,
  timezone,
  emergencyActions,
  onEmergencyAction,
  savedCount = 0,
  onSavedClick,
  curatedListsCount = 0,
  onCuratedListsClick,
  sponsors,
  onSponsorClick,
  onSponsorImpression,
  multilingualEnabled: _multilingualEnabled,
  availableLanguages,
  brandAccentCtaSecondary,
  brandAccentMenuIcons,
  brandAccentSocialCard,
  brandAccentSocialCardBg,
  brandAccentPoweredBy,
  brandPrimary,
}: MorePageProps) {
  const { t, i18n } = useTranslation("map");
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as VisitorSearchParams;
  const {
    status: consentStatus,
    accept: acceptConsent,
    decline: declineConsent,
  } = useAnalyticsConsent();
  const [consentModalOpen, setConsentModalOpen] = useState(false);

  // CTA items (first 2 across all sections, by sort_order)
  const ctaItems = useMemo<MenuItem[]>(() => {
    return menuConfig.sections
      .flatMap((s) => s.items)
      .filter((it) => it.is_cta && it.url && it.url.trim() !== "")
      .sort((a, b) => a.sort_order - b.sort_order)
      .slice(0, 2)
      .map((it) => ({ ...it, url: it.url.replace("{slug}", mapSlug) }));
  }, [menuConfig.sections, mapSlug]);

  // Non-CTA sections for "PLAN YOUR VISIT"
  const planSections = useMemo<MenuSection[]>(() => {
    return menuConfig.sections
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((s) => ({
        ...s,
        items: s.items.filter((it) => !it.is_cta),
      }));
  }, [menuConfig.sections]);

  // Flat first-4 sponsors for teaser grid
  const teaserSponsors = useMemo<SponsorLogo[]>(() => {
    if (!sponsors) return [];
    return sponsors.flatMap((tier) => tier.sponsors).slice(0, 4);
  }, [sponsors]);

  const allSponsors = useMemo<SponsorLogo[]>(
    () => (sponsors ? sponsors.flatMap((tier) => tier.sponsors) : []),
    [sponsors],
  );

  // Fire impression once per mount of main view
  const impressionFiredRef = useRef(false);
  useEffect(() => {
    if (impressionFiredRef.current) return;
    if (allSponsors.length === 0) return;
    onSponsorImpression?.(allSponsors, "more_page");
    impressionFiredRef.current = true;
  }, [allSponsors, onSponsorImpression]);

  // Preload emergency icons
  useEffect(() => {
    if (emergencyActions?.length) {
      registerThemeIcons(emergencyActions.map((a) => a.icon));
    }
  }, [emergencyActions]);

  const hasHours = !!(operatingMode && operatingConfig && timezone);
  const hasEmergency = !!(emergencyActions && emergencyActions.length > 0);
  const hasFAQ = !!(faqConfig?.enabled && faqConfig.items.length > 0);
  const hasSaved = savedCount > 0 && !!onSavedClick;
  const hasCuratedLists = curatedListsCount > 0 && !!onCuratedListsClick;
  const hasQuickLinks = hasSaved || hasCuratedLists || hasFAQ;
  const hasSponsors = teaserSponsors.length > 0;
  const hasSocial =
    !!menuConfig.social_links &&
    Object.values(menuConfig.social_links).some((v) => v != null);

  const navigateToView = (nextView: "faqs" | "sponsors") => {
    navigate({ to: ".", search: { ...search, view: nextView } });
  };

  return (
    <div
      className="absolute inset-0 z-10 bg-background overflow-y-auto"
      style={{ paddingBottom: "var(--bottom-nav-height, 0px)" }}
    >
      <VisitorPageHeader
        logoUrl={logoUrl}
        appName={appName}
        showShare
        shareUrl={shareUrl}
        shareTitle={shareTitle}
        shareText={shareText}
        notifications={notifications}
        onBellClick={onBellClick}
        isTrayOpen={isTrayOpen}
        mapSlug={mapSlug}
      />

      <div className="pb-6">
        {/* Hours */}
        {hasHours && (
          <OperatingHoursMenuSection
            operatingMode={operatingMode!}
            operatingConfig={operatingConfig!}
            timezone={timezone!}
          />
        )}

        {/* CTAs */}
        {ctaItems.length > 0 && (
          <div className="px-4 py-4 border-b flex gap-2">
            {ctaItems.map((item, idx) => {
              const label = getTranslatedValue(
                item.label,
                item.labelTranslations as NameTranslations,
                i18n.language,
              );
              const isPrimary = idx === 0;
              return (
                <a
                  key={item.id}
                  href={item.url}
                  target={item.open_in_new_tab ? "_blank" : undefined}
                  rel={item.open_in_new_tab ? "noopener noreferrer" : undefined}
                  onClick={() =>
                    analytics.clickOutbound({
                      url: item.url,
                      label: item.label,
                      location: "more_cta",
                    })
                  }
                  className={cn(
                    "flex-1 inline-flex items-center justify-center rounded-full min-h-[44px] px-4 text-sm font-medium transition-colors",
                    isPrimary
                      ? "text-white hover:opacity-90"
                      : brandAccentCtaSecondary
                        ? "bg-transparent border hover:bg-muted/50"
                        : "bg-transparent border border-border text-foreground hover:bg-muted/50",
                  )}
                  style={
                    isPrimary
                      ? { backgroundColor: brandPrimary ?? "var(--primary)" }
                      : brandAccentCtaSecondary
                        ? {
                            borderColor: brandAccentCtaSecondary,
                            color: brandAccentCtaSecondary,
                          }
                        : undefined
                  }
                >
                  {label}
                </a>
              );
            })}
          </div>
        )}

        {/* Emergency */}
        {hasEmergency && (
          <div className="pt-3 pb-2 border-b">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1 px-4">
              {t("menu.emergency")}
            </h3>
            {emergencyActions!.map((action) => {
              const isCall = action.actionType === "call";
              const iconDef = getIconDefinition(action.icon) ?? faPhone;
              const label = getTranslatedValue(
                action.label,
                action.labelTranslations as NameTranslations,
                i18n.language,
              );

              if (isCall) {
                return (
                  <a
                    key={action.id}
                    href={`tel:${action.target}`}
                    onClick={() => onEmergencyAction?.(action)}
                    className="flex items-center gap-3 w-full px-4 py-3 min-h-[44px] bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <FontAwesomeIcon
                      icon={iconDef}
                      className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400"
                    />
                    <span className="text-sm font-medium text-red-900 dark:text-red-100">
                      {label}
                    </span>
                  </a>
                );
              }

              return (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => onEmergencyAction?.(action)}
                  className="flex items-center gap-3 w-full px-4 py-3 min-h-[44px] text-left hover:bg-muted/50 transition-colors"
                >
                  <FontAwesomeIcon
                    icon={iconDef}
                    className={cn(
                      "h-5 w-5 shrink-0",
                      !brandAccentMenuIcons && "text-muted-foreground",
                    )}
                    style={brandAccentMenuIcons ? { color: brandAccentMenuIcons } : undefined}
                  />
                  <span className="text-sm">{label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Quick Links */}
        {hasQuickLinks && (
          <div className="py-2 border-b">
            {hasSaved && (
              <button
                type="button"
                onClick={onSavedClick}
                className="flex items-center gap-3 w-full px-4 py-3 min-h-[44px] text-left hover:bg-muted/50 transition-colors"
              >
                <FontAwesomeIcon
                  icon={faHeart}
                  className={cn(
                    "h-5 w-5",
                    brandAccentMenuIcons ? "fill-current" : "fill-red-500 text-red-500",
                  )}
                  style={brandAccentMenuIcons ? { color: brandAccentMenuIcons } : undefined}
                />
                <span className="text-sm">
                  {t("menu.mySaved", { count: savedCount })}
                </span>
              </button>
            )}

            {hasCuratedLists && (
              <button
                type="button"
                onClick={onCuratedListsClick}
                className="flex items-center gap-3 w-full px-4 py-3 min-h-[44px] text-left hover:bg-muted/50 transition-colors"
              >
                <FontAwesomeIcon
                  icon={faListCheck}
                  className={cn(
                    "h-5 w-5",
                    !brandAccentMenuIcons && "text-muted-foreground",
                  )}
                  style={brandAccentMenuIcons ? { color: brandAccentMenuIcons } : undefined}
                />
                <span className="text-sm">{t("menu.browseLists")}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {curatedListsCount}
                </span>
              </button>
            )}

            {hasFAQ && (
              <button
                type="button"
                onClick={() => navigateToView("faqs")}
                className="flex items-center gap-3 w-full px-4 py-3 min-h-[44px] text-left hover:bg-muted/50 transition-colors"
              >
                <FontAwesomeIcon
                  icon={faCircleQuestion}
                  className={cn(
                    "h-5 w-5",
                    !brandAccentMenuIcons && "text-muted-foreground",
                  )}
                  style={brandAccentMenuIcons ? { color: brandAccentMenuIcons } : undefined}
                />
                <span className="text-sm">
                  {getTranslatedValue(
                    faqConfig!.title || t("menu.faq"),
                    faqConfig!.titleTranslations as NameTranslations,
                    i18n.language,
                  )}
                </span>
              </button>
            )}
          </div>
        )}

        {/* PLAN YOUR VISIT — admin menu sections (CTA items already filtered out) */}
        {planSections.map((section) => (
          <VisitorMenuSection
            key={section.id}
            section={section}
            onClose={() => {}}
            mapSlug={mapSlug}
            brandAccentMenuIcons={brandAccentMenuIcons}
          />
        ))}

        {/* Sponsors teaser */}
        {hasSponsors && (
          <div className="pt-4 pb-3 px-4 border-b">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-3">
              {t("menu.ourSponsors")}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {teaserSponsors.map((sponsor) => (
                <SponsorCard
                  key={sponsor.id}
                  sponsor={sponsor}
                  onClick={onSponsorClick}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => navigateToView("sponsors")}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-3 mt-2 min-h-[44px]"
            >
              {t("sponsors.viewAll")}
            </button>
          </div>
        )}

        {/* Social card */}
        {hasSocial && (
          <div
            className="mx-4 my-4 rounded-2xl p-4 text-center"
            style={{
              backgroundColor:
                brandAccentSocialCardBg ??
                "color-mix(in srgb, var(--primary) 16%, transparent)",
            }}
          >
            <p
              className="text-sm font-medium mb-1"
              style={
                brandAccentSocialCard
                  ? { color: brandAccentSocialCard }
                  : undefined
              }
            >
              {t("more.followUs", { org: orgName, defaultValue: `Follow ${orgName}` })}
            </p>
            <SocialLinksRow
              socialLinks={menuConfig.social_links}
              brandedColor={brandAccentSocialCard ?? null}
            />
          </div>
        )}

        {/* Powered-by Wondermaps — gated only on org's showAttribution */}
        {showAttribution && (
          <div className="flex items-center justify-center px-4 py-3 border-t border-border/50">
            <a
              href="https://www.wondermaps.co"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              style={
                brandAccentPoweredBy
                  ? { color: brandAccentPoweredBy }
                  : undefined
              }
            >
              {t("common:poweredByWondermaps")}
            </a>
          </div>
        )}

        {/* Utility: language, cookie prefs, terms/privacy */}
        <div className="border-t">
          {(availableLanguages?.length ?? 0) > 1 && (
            <div className="px-4 py-2">
              <LanguagePicker availableLanguages={availableLanguages} />
            </div>
          )}

          <button
            type="button"
            onClick={() => setConsentModalOpen(true)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left px-4 py-3 min-h-[44px] w-full"
          >
            {t("common:consent.cookiePreferences")}
          </button>

          <div className="px-4 pt-1 pb-3 space-y-0.5 border-t border-border/50">
            <a
              href="https://www.wondermaps.co/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors block py-1.5"
            >
              {t("common:terms")}
            </a>
            <a
              href="https://www.wondermaps.co/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors block py-1.5"
            >
              {t("common:privacy")}
            </a>
          </div>
        </div>

      </div>

      <ConsentModal
        open={consentModalOpen}
        onOpenChange={setConsentModalOpen}
        status={consentStatus}
        onAccept={() => {
          acceptConsent("modal");
          setConsentModalOpen(false);
        }}
        onDecline={() => {
          declineConsent("modal");
          setConsentModalOpen(false);
        }}
      />
    </div>
  );
}

function SponsorCard({
  sponsor,
  onClick,
}: {
  sponsor: SponsorLogo;
  onClick?: (sponsor: SponsorLogo, placement: string) => void;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  if (imgFailed && !sponsor.name) return null;

  return (
    <a
      href={sponsor.websiteUrl || undefined}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (!sponsor.websiteUrl) e.preventDefault();
        onClick?.(sponsor, "more_page");
      }}
      className="flex flex-col items-center justify-center gap-1.5 bg-white dark:bg-muted rounded-2xl h-28 px-3 py-2 shadow-xs hover:shadow-sm transition-shadow"
    >
      {sponsor.logoUrl && !imgFailed ? (
        <img
          src={sponsor.logoUrl}
          alt={sponsor.name || "Sponsor"}
          className="max-h-14 max-w-full object-contain"
          onError={() => setImgFailed(true)}
        />
      ) : null}
      {(sponsor.showName !== false || imgFailed || !sponsor.logoUrl) && sponsor.name && (
        <span className="text-xs text-muted-foreground truncate max-w-full">
          {sponsor.name}
        </span>
      )}
    </a>
  );
}

